import React, { useEffect, useState } from "react";
import { Box, Dialog, DialogContent, Divider, FormControl, FormControlLabel, FormHelperText, Grid2, MenuItem, Radio, RadioGroup, Select, styled, TextField, Typography } from "@mui/material";
import SingleEntityCurrentStatus from "./SingleEntityCurrentStatus";
import MultipleEntityCurrentStatus from "./MultipleEntityCurrentStatus";
import MultipleEntityUpdatedStatus from "./MultipleEntityUpdatedStatus";
import SingleEntityUpdatedStatus from "./SingleEntityUpdatedStatus";
import OperationalStatusActionBadge from "./OperationalStatusActionBadge";
import { useToast } from "../../Hooks/useToast";
import { useDispatch } from "react-redux";
import { SetSessionExpired } from "../../Actions";
import CloseButton from "../CloseButton";
import CustomIcon from "../CustomIcon";
import ErrorIcon from "@mui/icons-material/Error";
import { BatchActionIconButton, PrimaryFormLoadingActionButton } from "../../Features/Table/GenericDraggableTable/TableStyles";
import { PrimaryTooltip } from "../StyledComponent";
import { bulkAssignTags, getAvailableTags, getBulkEntityTags, getEntityTags, updateEntityTags } from "../../Api/TagsApi";
import { getReplacementStatus } from "../../Api/ServiceRecordApi";
import { validateBatteryReplacementAnswer } from "../../Helper/DigitalTwin/BatteryComponentsHelper";
import EditWithBorderIcon from "../Icons/DigitalTwin/EditWithBorderIcon";
import { useBaseEntityInfo } from "../../Hooks/usePagesContent";
import { countStatuses, getNoChangeErrors, getStatusOptions, getTagSaveResult, isSingleEntityUpdate, loadStatusDialogData, NON_OPERATIONAL, OPERATIONAL } from "../../Helper/OperationalStatus/statusOptions";

const MAX_COMMENT_LENGTH = 1000;

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiPaper-root": {
        borderRadius: 8,
        boxShadow: theme.shadows[5],
        width: "560px",
        maxWidth: "90%",
        padding: '24px'
    },
}));

// Custom styled Radio
const StyledRadio = styled(Radio)(() => ({
    padding: 0,
    marginRight: "8px", // spacing between radio circle and label
    '& .MuiSvgIcon-root': {
        fontSize: 20,
    },
}));

// Custom styled FormControlLabel
const StyledFormControlLabel = styled(FormControlLabel)((theme) => ({
    margin: 0,
    flexDirection: 'row-reverse',
    width: '100%',
    justifyContent: 'space-between',

    "& .MuiFormControlLabel-label": {
        fontFamily: theme.primaryFontV2,
        color: "#15191E",
        fontSize: "12px",
        lineHeight: "16px",
    },
}));

// Custom styled Select
const StyledSelect = styled(Select)((theme) => ({
    "& .MuiOutlinedInput-input .MuiTypography-root": {
        fontSize: '14px',
    },

    "& .MuiSvgIcon-root": {
        fontSize: '27px',
    },

    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: '#E6E6E6',
        borderWidth: "1px",
        borderRadius: '8px',
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#6D72F6",
        borderWidth: "1px",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#6D72F6",
        borderWidth: "1px",
    },
    "&.Mui-focused .MuiOutlinedInput-input .MuiTypography-root": {
        color: "#6D72F6",
    },
    "&.Mui-focused .MuiSvgIcon-root": {
        color: "#6D72F6",
    },
}));

// Custom styled TextField
const StyledTextField = styled(TextField)((theme) => ({
    "& .MuiOutlinedInput-root": {
        fontSize: "12px",
        lineHeight: "24px",
        padding: 0,

        "& fieldset": {
            borderColor: "#E6E6E6",
            borderWidth: "1px",
            borderRadius: '8px',
        },
        "&:hover fieldset": {
            borderColor: "#6D72F6",
            borderWidth: "1px",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#6D72F6",
            borderWidth: "1px",
        },
        "& textarea": {
            padding: '8px 16px',
            fontSize: "14px",
            lineHeight: "20px",
        },
        "& textarea::placeholder": {
            color: "#A4B2C1",
            opacity: 1,
        },
    },
    '& .MuiList-root': {
        padding: 0,
    }
}));

const OperationalStatusAction = ({
    entityType = 'battery',
    entityIds,
    vehicleNumber,
    deviceID,
    isActive,
    isInventoryPage = false,
    onUpdate = () => { },
    isSelectAll = false,
    totalCount = 1,
    selectedFilters,
    readOnly = false,
    // Opt-in "battery replaced during downtime?" cross-check. When vehicleIds
    // are supplied (single-vehicle case), the modal shows the Yes/No question
    // and validates it against recorded replacements. onRecordNow deep-links to
    // the Components tab. Omitted by existing mounts => feature stays off.
    vehicleIds = [],
    onRecordNow,
    // Optional: entity tags fetched by the parent, so the page makes one entity-tags
    // request instead of two. Tri-state, and the distinction matters:
    //   undefined - caller does not preload; fetch our own, exactly as before.
    //   null      - caller preloads but has not resolved yet; wait, do NOT fetch.
    //   array     - use these.
    // Without the null case this raced: the parent's fetch and ours are independent, so
    // whichever resolved first decided whether we duplicated the request.
    preloadedTags,
    // The page's global filters and their module (as passed to getDigitalTwinList). A
    // select-all update re-runs the list on the backend, which reads these from the
    // request headers; without them it updates every entity the user can access.
    globalFilters = {},
    globalFilterModule = null,
}) => {
    const usesPreloadedTags = preloadedTags !== undefined;
    const isSingleEntity = isSingleEntityUpdate({ isSelectAll, entityIds });
    const [isOpen, setIsOpen] = useState(false);
    const { baseEntityTypePluralLable, baseEntityTypeLable, baseEntityType } = useBaseEntityInfo();
    const [singleEntityInitialOperationalStatusData, setSingleEntityInitialOperationalStatusData] = useState({});
    const [bulkEntityInitialOperationalStatusCounts, setBulkEntityInitialOperationalStatusCounts] = useState(() => countStatuses());
    const [bulkEntityInitialTags, setBulkEntityInitialTags] = useState({});
    const [newOperationalStatus, setNewOperationalStatus] = useState('');
    const [newComment, setNewComment] = useState('');
    const [newReasonForNonOperational, setNewReasonForNonOperational] = useState('');
    const [isMultipleEntitiesMode, setIsMultipleEntitiesMode] = useState(isSelectAll || entityIds.length > 1);
    const [showPreviewMode, setShowPreviewMode] = useState(false);
    const [deviceId, setDeviceId] = useState(vehicleNumber);
    const [nonOperationalReasonOptions, setNonOperationalReasonOptions] = useState({});
    const [statusOptions, setStatusOptions] = useState(() => getStatusOptions());
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [batteryReplaced, setBatteryReplaced] = useState(""); // "" | "yes" | "no"
    const [replacementStatusMap, setReplacementStatusMap] = useState({});

    const { openNotification } = useToast();
    const dispatch = useDispatch();

    // Cross-check only for the single-vehicle case (bulk needs a vehicle->entity
    // map the mount does not currently supply).
    const replacementCheckEnabled =
        Array.isArray(vehicleIds) && vehicleIds.length === 1 && isSingleEntity;
    const hasReplacementSince = !!replacementStatusMap[vehicleIds[0]]?.hasReplacementSince;
    const showReplacementQuestion = replacementCheckEnabled && newOperationalStatus === OPERATIONAL;
    // Reactive cross-check: note + Update-disable appear the moment a mismatching
    // answer is picked (not only on submit). null when consistent.
    const replacementError = showReplacementQuestion
        ? validateBatteryReplacementAnswer({ answer: batteryReplaced, hasReplacementSince })
        : null;

    const handleClose = () => {
        // A save in flight would close and reset the dialog again when it returns.
        if (isSaving) return;
        setShowPreviewMode(false);
        setIsOpen(false);
        resetForm();
    }

    const getDataToSave = () => {
        const dataToSave = [
            {
                "tagCode": "status",
                "valueCode": newOperationalStatus,
                "notes": newComment,
            }
        ];
        dataToSave.push({
            "tagCode": "sub_status",
            "valueCode": newOperationalStatus === NON_OPERATIONAL ? newReasonForNonOperational : null
        });

        return {
            tags: dataToSave,
            // Best-effort: carried so the backend can persist "replaced during
            // downtime" if/when supported (not part of the fixed tag contract).
            ...(showReplacementQuestion ? { batteryReplacedDuringDowntime: batteryReplaced } : {}),
        };
    }

    const resetForm = () => {
        setNewComment('');
        setNewOperationalStatus('');
        setNewReasonForNonOperational('');
        setBatteryReplaced('');
        setErrors({});
    }

    const handleUpdate = async () => {
        // validation
        let errorsData = {}
        if (!newOperationalStatus) {
            errorsData['newOperationalStatus'] = true
        }
        if (!newComment) {
            errorsData['newComment'] = true
        }
        if (newComment.length > MAX_COMMENT_LENGTH) {
            errorsData['newCommentCount'] = true
        }
        if (newOperationalStatus === NON_OPERATIONAL && !newReasonForNonOperational) {
            errorsData['newReasonForNonOperational'] = true
        }

        Object.assign(errorsData, getNoChangeErrors({
            isSingleEntity,
            newStatus: newOperationalStatus,
            newReason: newReasonForNonOperational,
            singleEntityData: singleEntityInitialOperationalStatusData,
            bulkCounts: bulkEntityInitialOperationalStatusCounts,
            bulkTags: bulkEntityInitialTags,
        }));

        // Battery-replacement cross-check (single-vehicle, marking operational).
        if (showReplacementQuestion) {
            const key = validateBatteryReplacementAnswer({
                answer: batteryReplaced,
                hasReplacementSince,
            });
            if (key) errorsData['batteryReplaced'] = key;
        }

        setErrors(errorsData);
        if (Object.keys(errorsData).length) {
            return;
        }

        if (!showPreviewMode) {
            setShowPreviewMode(true);
        }
    }

    const handleConfirmUpdate = async () => {
        if (isSaving) return;
        const dataToSave = getDataToSave();
        let saveResult;
        setIsSaving(true);
        try {
            const res = isSingleEntity
                ? await updateEntityTags(entityType, entityIds[0], dataToSave)
                : await bulkAssignTags(entityType, entityIds, dataToSave, isSelectAll, selectedFilters, globalFilters, globalFilterModule);
            saveResult = getTagSaveResult(res);
        } catch (err) {
            console.error("Failed to update operational status:", err);
            saveResult = getTagSaveResult(null);
        } finally {
            setIsSaving(false);
        }
        if (saveResult.sessionExpired) {
            dispatch(SetSessionExpired(true));
            return;
        }
        if (!saveResult.ok) {
            // Keep the dialog on the preview so the user can retry.
            openNotification({ text: saveResult.message, icon: 'toast-error' });
            return;
        }
        setShowPreviewMode(false);
        setIsOpen(false);
        resetForm();
        openNotification({ text: 'Update Successful', icon: 'toast-success' })
        onUpdate();
        if (!isInventoryPage) {
            // reload to see latest data
            if (isSingleEntity) {
                // With a preloading parent, onUpdate() above triggers its refetch and the
                // new tags arrive as props - fetching here as well would double the call.
                if (!usesPreloadedTags) fetchSingleEntityData();
            } else {
                fetchBulkEntityData();
            }
        }
    }

    const processSingleEntityTags = (tags) => {
        let operationalStatusData = {}
        tags.forEach(tag => {
            if (tag.tagCode === 'status') {
                operationalStatusData['comment'] = tag?.customNotes;
                operationalStatusData['statusTime'] = tag?.updatedAt;
                operationalStatusData['status'] = tag?.valueCode;
                operationalStatusData['label'] = tag?.valueDisplayname;
                operationalStatusData['updatedBy'] = tag?.assignedBy;
            }
            if (tag.tagCode === 'sub_status') {
                operationalStatusData['reasonForNonOperational'] = tag?.valueCode ?? '';
            }
        })
        setSingleEntityInitialOperationalStatusData(operationalStatusData);
    }

    const processBulkEntityTags = (tags) => {
        const selectedEntitiesCount = isSelectAll ? totalCount : entityIds.length;
        setBulkEntityInitialTags(tags);
        setBulkEntityInitialOperationalStatusCounts(countStatuses(tags, selectedEntitiesCount));
    }

    const processOrganizationTags = (tags) => {
        const nonOperationalSubStatus = tags.find(tag => tag.tagCode === 'sub_status');
        let nonOperationalReasons = {};
        (nonOperationalSubStatus?.supportedValues || []).forEach(value => {
            nonOperationalReasons = {
                ...nonOperationalReasons,
                [value.valueCode]: value.displayName,
            }
        });
        setNonOperationalReasonOptions(nonOperationalReasons);
        setStatusOptions(getStatusOptions(tags));
    }

    const fetchSingleEntityData = async () => {
        const entityTagsRes = await getEntityTags(entityType, entityIds[0]);
        processSingleEntityTags(entityTagsRes?.response?.data?.tags || []);
    }

    const fetchBulkEntityData = async () => {
        const entityTagsRes = await getBulkEntityTags(entityType, entityIds, isSelectAll, selectedFilters, globalFilters, globalFilterModule);
        processBulkEntityTags(entityTagsRes?.response?.data?.tags || []);
    }

    const fetchEntityData = async () => {
        if (isSingleEntity) {
            // Skipped entirely when the parent preloads: the effect below consumes its
            // tags instead, including every later refresh.
            if (!usesPreloadedTags) await fetchSingleEntityData();
        } else {
            await fetchBulkEntityData();
        }
    }

    const fetchOrganizationTags = async () => {
        const organizationTagsRes = await getAvailableTags();
        processOrganizationTags(organizationTagsRes?.response?.data?.tags || []);
    }

    const fetchData = () => loadStatusDialogData({
        loadEntityTags: fetchEntityData,
        loadStatusOptions: fetchOrganizationTags,
    });

    // Fetched fresh each time the modal opens so a replacement recorded via the
    // "Record now" deep link is reflected on reopen.
    const fetchReplacementStatus = async () => {
        if (!Array.isArray(vehicleIds) || !vehicleIds.length) return;
        try {
            const rs = await getReplacementStatus(vehicleIds);
            const list = rs?.response?.data ?? rs?.response ?? [];
            const map = {};
            (Array.isArray(list) ? list : []).forEach((v) => {
                if (v?.vehicleID !== undefined) map[v.vehicleID] = v;
            });
            setReplacementStatusMap(map);
        } catch (err) {
            console.error("Failed to load replacement status:", err);
        }
    }

    const setupData = () => {
        setIsMultipleEntitiesMode(!isSingleEntity);
        fetchData();
        if (isSingleEntity) {
            setDeviceId(baseEntityType.toLowerCase() === "vehicle" ? vehicleNumber : deviceID);
        }
    }

    useEffect(() => {
        if (entityIds.length && isInventoryPage && isOpen) {
            setupData();
        }
    }, [isOpen, entityIds.length])

    useEffect(() => {
        if (entityIds.length && !isInventoryPage) {
            setupData();
        }
    }, [isInventoryPage, entityIds.length])

    // Consume the parent's tags on arrival and on every later refresh. Separate from
    // setupData so a refresh does not also re-request the org-wide available tags.
    useEffect(() => {
        if (usesPreloadedTags && preloadedTags && isSingleEntity) {
            processSingleEntityTags(preloadedTags);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preloadedTags])

    useEffect(() => {
        if (isOpen) {
            fetchReplacementStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    return (
        <>
            {isInventoryPage ? (
                <PrimaryTooltip
                    title={isActive ? <>Update Operational Status</> : <>Update Operational Status <br /> (Select minimum 1 row)</>}
                    placement="bottom"
                >
                    <BatchActionIconButton
                        onClick={() => setIsOpen(true)}
                        active={isActive}
                        disabled={!isActive}
                    >
                        <EditWithBorderIcon sx={{ fontSize: '12px !important' }} />
                    </BatchActionIconButton>
                </PrimaryTooltip>
            ) : (
                <OperationalStatusActionBadge
                    status={singleEntityInitialOperationalStatusData['status']}
                    label={singleEntityInitialOperationalStatusData['label']}
                    reasonForNonOperational={nonOperationalReasonOptions[singleEntityInitialOperationalStatusData['reasonForNonOperational']]}
                    onClick={() => setIsOpen(true)}
                    readOnly={readOnly}
                />
            )}
            <StyledDialog open={isOpen} onClose={() => { }}>
                <DialogContent sx={{ p: 0, minHeight: "350px" }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="fw600s16c000000" sx={{ fontSize: '20px', lineHeight: '20px' }}>
                            {isMultipleEntitiesMode ? `Multi-${baseEntityTypeLable} Operational Status Update` : `${baseEntityTypeLable} Operational Status Update`}
                        </Typography>
                        <CloseButton onClick={handleClose} />
                    </Box>
                    {isMultipleEntitiesMode ? (
                        <MultipleEntityCurrentStatus
                            entityStatusCounts={bulkEntityInitialOperationalStatusCounts}
                            statusOptions={statusOptions}
                        />
                    ) : (
                        <SingleEntityCurrentStatus
                            deviceId={deviceId}
                            data={singleEntityInitialOperationalStatusData}
                            reasonOptions={nonOperationalReasonOptions}
                        />
                    )}
                    {!showPreviewMode && (
                        <Box sx={{ mb: 3 }}>
                            {isMultipleEntitiesMode && (
                                <Grid2 container gap={1} alignItems={'center'} sx={{ bgcolor: '#FFFBEC', borderRadius: 2, px: 2, py: 1.5 }}>
                                    <CustomIcon name="alert-circle-filled-icon" />
                                    <Typography variant="fw400s14c000000" sx={{ lineHeight: '24px', fontWeight: 500, color: '#AD7F00' }}>
                                        Note: Status changes made will apply to all {baseEntityTypePluralLable} selected
                                    </Typography>
                                </Grid2>
                            )}
                            <Divider sx={{ my: 2, borderColor: '#E6E6E6' }} />
                            <Box>
                                <FormControl sx={{ width: '100%' }} error={false}>
                                    <Box sx={{ mb: 1 }}>
                                        <Typography variant="fw600s16c000000" sx={{ fontWeight: 700, lineHeight: '24px' }}>1. New Operational Status</Typography>
                                        <Typography variant="fw500s16lh24c000000" sx={{ fontWeight: 700, color: '#B50500' }}>&nbsp;*</Typography>
                                    </Box>
                                    <RadioGroup
                                        aria-labelledby={`operational-status`}
                                        value={newOperationalStatus}
                                        name={`operational-status`}
                                        sx={{ gap: 1 }}
                                        onChange={(e) => {
                                            setNewOperationalStatus(e.target.value)
                                        }}
                                    >
                                        <Grid2 container gap={0.5} pl={3} flexDirection={'column'}>
                                            {statusOptions.map((option) => (
                                                <StyledFormControlLabel
                                                    key={option.value}
                                                    value={option.value}
                                                    data-testid={`operational-status-option-${option.value}`}
                                                    control={<StyledRadio />}
                                                    label={
                                                        <Typography variant="fw400s14c494949" sx={{ lineHeight: '24px' }}>{option.label}</Typography>
                                                    }
                                                />
                                            ))}
                                        </Grid2>
                                    </RadioGroup>
                                    {errors['newOperationalStatus'] && (
                                        <FormHelperText sx={{ fontSize: '10px', color: '#930500' }}>* Please select an option</FormHelperText>
                                    )}
                                    {errors['noChangeInOperationalStatus'] && (
                                        <FormHelperText sx={{ fontSize: '10px', color: '#930500' }}>* Old and new status must be different</FormHelperText>
                                    )}
                                </FormControl>
                                {newOperationalStatus === NON_OPERATIONAL && (
                                    <FormControl sx={{ width: '100%', mt: 2 }}>
                                        <Grid2 container flexDirection={'column'} gap={0.75} pl={3}>
                                            <Typography variant="fw600s14c000000" sx={{ lineHeight: '24px' }}>Select Reason</Typography>
                                            <StyledSelect
                                                value={newReasonForNonOperational}
                                                onChange={(e) => {
                                                    setNewReasonForNonOperational(e.target.value)
                                                }}
                                                displayEmpty
                                                error={errors['newReasonForNonOperational'] || errors['noChangeInReasonForNonOperational']}
                                                inputProps={{ 'aria-label': 'Without label' }}
                                            >
                                                <MenuItem value="" sx={{ py: 1, pl: 1.5 }}>
                                                    <Typography variant="fw400s12c3D4B5A" sx={{ lineHeight: '16px' }}>Select Reason</Typography>
                                                </MenuItem>
                                                {Object.keys(nonOperationalReasonOptions).map(key => (
                                                    <MenuItem value={key} sx={{ py: 1, pl: 1.5 }}>
                                                        <Typography variant="fw400s12c3D4B5A" sx={{ lineHeight: '16px' }}>{nonOperationalReasonOptions[key]}</Typography>
                                                    </MenuItem>
                                                ))}
                                            </StyledSelect>
                                            {errors['newReasonForNonOperational'] && (
                                                <FormHelperText sx={{ fontSize: '10px', color: '#930500', ml: 0, mt: -0.5 }}>{'Please select a reason'}</FormHelperText>
                                            )}
                                            {errors['noChangeInReasonForNonOperational'] && (
                                                <FormHelperText sx={{ fontSize: '10px', color: '#930500', ml: 0, mt: -0.5 }}>* Old and new reason must be different</FormHelperText>
                                            )}
                                        </Grid2>
                                    </FormControl>
                                )}
                            </Box>
                            {showReplacementQuestion && (
                                <>
                                    <Divider sx={{ my: 2, borderColor: '#E6E6E6' }} />
                                    <Box>
                                        <Typography sx={{ fontSize: '16px', fontWeight: 700, lineHeight: '24px', color: '#000', display: 'block', mb: 1 }}>
                                            Battery replacement
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: '24px', color: '#000', display: 'block', mb: 1 }}>
                                            Battery pack(s) replaced during this downtime?
                                        </Typography>
                                        <Box sx={{ display: 'flex', border: '1px solid #E0E0E0', borderRadius: '8px', overflow: 'hidden' }}>
                                            {[{ v: 'yes', label: 'Yes' }, { v: 'no', label: 'No' }].map((opt, idx) => (
                                                <Box
                                                    key={opt.v}
                                                    data-testid={`battery-replaced-${opt.v}`}
                                                    onClick={() => setBatteryReplaced(opt.v)}
                                                    sx={{
                                                        flex: 1,
                                                        textAlign: 'center',
                                                        py: 1.25,
                                                        cursor: 'pointer',
                                                        fontSize: '14px',
                                                        fontWeight: batteryReplaced === opt.v ? 600 : 400,
                                                        color: batteryReplaced === opt.v ? '#6D72F6' : '#494949',
                                                        backgroundColor: batteryReplaced === opt.v ? '#EEF0FF' : '#fff',
                                                        borderLeft: idx === 1 ? '1px solid #E0E0E0' : 'none',
                                                    }}
                                                >
                                                    {opt.label}
                                                </Box>
                                            ))}
                                        </Box>
                                        {replacementError === 'yesButNone' && (
                                            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#FEF3F2', borderRadius: '8px', p: 1.5 }}>
                                                <ErrorIcon sx={{ color: '#D32F2F', fontSize: 18 }} />
                                                <Typography sx={{ fontSize: '13px', color: '#B50500', fontWeight: 500 }}>
                                                    Note: You have not recorded your battery replacement.
                                                    {onRecordNow && (
                                                        <Box
                                                            component="span"
                                                            onClick={onRecordNow}
                                                            data-testid="operational-record-now"
                                                            sx={{ color: '#6D72F6', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', ml: 0.5 }}
                                                        >
                                                            Record now
                                                        </Box>
                                                    )}
                                                </Typography>
                                            </Box>
                                        )}
                                        {replacementError === 'noButExists' && (
                                            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#FEF3F2', borderRadius: '8px', p: 1.5 }}>
                                                <ErrorIcon sx={{ color: '#D32F2F', fontSize: 18 }} />
                                                <Typography sx={{ fontSize: '13px', color: '#B50500', fontWeight: 500 }}>
                                                    Note: You have replaced your battery pack
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </>
                            )}
                            <Divider sx={{ my: 2.5, borderColor: '#E6E6E6' }} />
                            <Box>
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="fw600s16c000000" sx={{ fontWeight: 700, lineHeight: '24px' }}>2. Add Comments</Typography>
                                    <Typography variant="fw500s16lh24c000000" sx={{ fontWeight: 700, color: '#B50500' }}>&nbsp;*</Typography>
                                </Box>
                                <Grid2 container gap={0.5} pl={3} flexDirection={'column'}>
                                    <StyledTextField
                                        fullWidth
                                        autoComplete="off"
                                        variant="outlined"
                                        placeholder="Type here"
                                        value={newComment}
                                        onChange={(e) => {
                                            setNewComment(e.target.value)
                                        }}
                                        multiline
                                        minRows={5}
                                        sx={{ flex: 1, minWidth: "150px" }}
                                        error={errors['newComment'] || newComment.length > MAX_COMMENT_LENGTH}
                                    />
                                    {errors['newComment'] && (
                                        <FormHelperText sx={{ fontSize: '10px', color: '#930500', ml: 0, mt: -0.5 }}>{'Please enter comments'}</FormHelperText>
                                    )}
                                    <Typography
                                        variant="fw400s10cB50500"
                                        sx={{ color: newComment.length > MAX_COMMENT_LENGTH ? '#930500 !important' : '#8D8D8D !important' }}
                                    >
                                        Only {MAX_COMMENT_LENGTH} characters are allowed: {newComment.length}/{MAX_COMMENT_LENGTH} characters
                                    </Typography>
                                </Grid2>
                            </Box>
                        </Box>
                    )}
                    {showPreviewMode && (
                        isMultipleEntitiesMode ? (
                            <MultipleEntityUpdatedStatus
                                vehicleCount={bulkEntityInitialOperationalStatusCounts['total']}
                                status={newOperationalStatus}
                                reason={nonOperationalReasonOptions[newReasonForNonOperational]}
                                comment={newComment}
                            />
                        ) : (
                            <SingleEntityUpdatedStatus
                                deviceId={deviceId}
                                status={newOperationalStatus}
                                reason={nonOperationalReasonOptions[newReasonForNonOperational]}
                                comment={newComment}
                            />
                        )
                    )}
                    <Grid2 container justifyContent={'flex-end'}>
                        <PrimaryFormLoadingActionButton
                            disabled={!showPreviewMode && !!replacementError}
                            onClick={() => {
                                if (!showPreviewMode) {
                                    handleUpdate();
                                } else {
                                    handleConfirmUpdate();
                                }
                            }}
                            loading={isSaving}
                            data-testid="operational-status-submit"
                            sx={{ borderRadius: '4px', px: 1.5 }}
                        >
                            {showPreviewMode ? 'Confirm Update' : 'Update'}
                        </PrimaryFormLoadingActionButton>
                    </Grid2>
                </DialogContent>
            </StyledDialog>
        </>
    )
}

export default OperationalStatusAction;
